/* eslint-disable max-len */
import {
    Box,
    Button,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Text,
    VStack,
} from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { ComponentProps, useMemo } from 'react';
import { KeyAvailableType } from '@acp/shared';
import { Alert } from '@acp/core-ui';
import { EditPropertyFormValues } from './form-schema';
import { PropertyAddressCard } from '../property-address-card';
import { usePropertyManagers } from '../../../agency';
import { usePropertyKeyPickupLocation } from '../../hooks/use-property';
import { KeyOnsiteConfirmation } from './key-onsite-confirmation';

function ConfirmPropertyEditScreen({
    propertyId,
    onBack,
    onSubmit,
    isLoading,
    error,
    showHeader = true,
    p,
    confirmButtonText,
    showAgencyKeyFields = true,
    showOnsiteKeyFields = true,
}: {
    propertyId: number;
    confirmButtonText?: string;
    onSubmit: (vals: EditPropertyFormValues) => void;
    onBack: () => void;
    isLoading: boolean;
    error: Error | null;
    showHeader?: boolean;
    p?: ComponentProps<typeof ModalBody>['p'];
    showAgencyKeyFields?: boolean;
    showOnsiteKeyFields?: boolean;
}) {
    const {
        getValues, handleSubmit,
        formState: { defaultValues },
    } = useFormContext<EditPropertyFormValues>();

    const { propertyManagerId, keyNumber, isKeyAvailable } = getValues().agencyKey ?? {
        propertyManagerId: '',
        keyNumber: null,
        isKeyAvailable: 'NO',
    };

    const propertyManagerQuery = usePropertyManagers();
    const propertyPickupLocation = usePropertyKeyPickupLocation(propertyId);

    const newPropertyManager = useMemo(() => {
        if (propertyManagerQuery.data === undefined) {
            return null;
        }

        return propertyManagerQuery.data.find((manager) => manager.id === propertyManagerId);
    }, [propertyManagerQuery.data]);

    return (
        <VStack align="left">
            { showHeader && (<ModalHeader>Confirm property update</ModalHeader>)}

            <ModalBody p={p}>
                { showHeader && (<PropertyAddressCard w="full" mb={5} propertyId={propertyId} />)}

                {showAgencyKeyFields && (
                    <Box w="full">
                        <VStack gap={6} pb={4}>
                            <Text>
                                Please confirm that all the information you have entered for the
                                property is accurate before proceeding with the submission.
                            </Text>

                            {propertyManagerId !== undefined && (
                                <Box w="full">
                                    <Text fontWeight="medium" fontSize="md">
                                        Property manager
                                    </Text>
                                    <Text>
                                        {newPropertyManager?.name}
                                        <br />
                                        {newPropertyManager?.email}
                                    </Text>
                                </Box>
                            )}

                            <Box w="full">
                                <Text fontWeight="medium" fontSize="md">
                                    Key number
                                </Text>
                                <Text>
                                    {defaultValues?.agencyKey?.keyNumber !== keyNumber && defaultValues?.agencyKey?.keyNumber !== ''
                                        ? (
                                            <>
                                                Changing from
                                                {' '}
                                                {
                                                    defaultValues?.agencyKey?.keyNumber === ''
                                                || defaultValues?.agencyKey?.keyNumber === undefined
                                                || defaultValues?.agencyKey?.keyNumber === null
                                                        ? <Text color="muted.300" as="span">N/A</Text>
                                                        : <Text textTransform="uppercase" as="span">{defaultValues?.agencyKey?.keyNumber}</Text>
                                                }
                                                {' '}
                                                to
                                                {' '}
                                                {
                                                    keyNumber === ''
                                                        ? <Text color="muted.300" as="span">N/A</Text>
                                                        : (
                                                            <Text textTransform="uppercase" as="span">{keyNumber}</Text>
                                                        )
                                                }
                                            </>
                                        )
                                        : <Text textTransform="uppercase" as="span">{keyNumber}</Text>}
                                </Text>
                            </Box>

                            {propertyPickupLocation.data !== undefined && (
                                <Box w="full">
                                    <Text fontWeight="medium" fontSize="md">
                                        Key pickup location
                                    </Text>

                                    {isKeyAvailable === KeyAvailableType.No
                                        ? <Text>This key is not available to be picked up</Text>
                                        : (
                                            <Text>
                                                {propertyPickupLocation.data.fullAddress}
                                            </Text>
                                        )}
                                </Box>
                            )}
                        </VStack>
                    </Box>
                )}

                {showOnsiteKeyFields && (
                    <KeyOnsiteConfirmation propertyId={propertyId} />
                )}
                {error !== null && (
                    <Alert status="error" title="An error occurred while submitting your request. Please try again." mt={5}>
                        {error.message}
                    </Alert>
                )}
            </ModalBody>

            <ModalFooter p={p} justifyContent="space-between">
                <Button
                    size="md"
                    colorScheme="muted"
                    variant="ghost"
                    onClick={onBack}
                >
                    Back
                </Button>
                <Button
                    colorScheme="secondary"
                    variant="solid"
                    size="md"
                    onClick={handleSubmit(onSubmit)}
                    isLoading={isLoading}
                >
                    {confirmButtonText ?? 'Update property'}
                </Button>
            </ModalFooter>
        </VStack>
    );
}

export { ConfirmPropertyEditScreen };
